/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Explore Our AI Tools section with grid layout and carousel
 */
import Image from 'next/image';
import Link from 'next/link';
import React, { useRef, useState } from 'react';

import {
  imageGenerateURL,
  videoGenerateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

interface Tool {
  id: string;
  imageUrl: string;
  title: string;
  description: string;
  link: string;
}

const ExploreToolsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const tools: Tool[] = [
    {
      id: 'ai-video-generator',
      imageUrl: 'https://assets.falcocut.ai/ai-product-avatar/ai-video-generator.webp',
      title: 'AI Video Generator',
      description:
        "Turn ideas into engaging videos with AI-powered creation in minutes. It's perfect for marketing, tutorials, and social media campaigns without the need for complex editing skills.",
      link: videoGenerateURL,
    },
    {
      id: 'ai-image-generator',
      imageUrl: 'https://assets.falcocut.ai/ai-product-avatar/ai-video-translator.webp',
      title: 'AI Image Generator',
      description:
        'Generate unique visuals instantly in seconds. Whether for design, marketing, or social media, this tool creates customizable, high-quality images that match your creative vision and bring ideas to life effortlessly.',
      link: imageGenerateURL,
    },
    {
      id: 'voice-cloning',
      imageUrl: 'https://assets.falcocut.ai/ai-product-avatar/ai-avatar-generator.webp',
      title: 'Voice Cloning',
      description:
        'Clone your voice in seconds and speak in any language. With natural AI speech synthesis, you can deliver consistent and authentic voiceovers for videos, podcasts, and more.',
      link: voiceCloneURL,
    },
  ];

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / tools.length;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex !== activeIndex) {
        setActiveIndex(currentIndex);
      }
    }
  };

  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / tools.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  const renderToolCard = (tool: Tool) => (
    <Link
      href={tool.link}
      className="block rounded-2xl overflow-hidden h-full flex flex-col"
    >
      {/* Tool Image */}
      <div className="relative w-full h-[158px] rounded-2xl overflow-hidden">
        <Image
          src={tool.imageUrl}
          alt={tool.title}
          fill
          className="object-cover"
          loading="lazy"
        />
      </div>

      {/* Tool Content */}
      <div className="p-6 pad:p-8 !pl-0 flex-1 flex flex-col">
        <h3 className="text-xl pad:text-2xl font-bold text-[#0F1117] mb-3 pad:mb-4">
          {tool.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-1">
          {tool.description}
        </p>
      </div>
    </Link>
  );

  return (
    <section className="w-full py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title Area */}
        <div className="text-center mb-10 pad:mb-12 sm:mb-16">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] mb-3 pad:mb-4">
            Explore Our AI Tools
          </h2>
        </div>

        {/* PC: 3-column grid */}
        <div className="hidden sm:grid sm:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <div key={tool.id}>{renderToolCard(tool)}</div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="sm:hidden">
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {tools.map((tool) => (
              <div key={tool.id} className="w-full flex-shrink-0 snap-center px-2">
                {renderToolCard(tool)}
              </div>
            ))}
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {tools.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleIndicatorClick(index)}
                className={`
                  h-2 rounded-full transition-all duration-300
                  ${activeIndex === index ? 'w-8 bg-[#3366FF]' : 'w-2 bg-gray-300'}
                `}
                aria-label={`Go to tool ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreToolsSection;
